import changePassword from 'api/changePassword'
import { Formik } from 'formik'
import { useCallback, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import onlyAllowedAndLatinLetters from 'utils/onlyAllowedAndLatinLetters'
import * as yup from 'yup'
import ErrorCode from './ErrorCodes'
import handleValidationError from './handleValidationError'
import validateMessage from './validateMessage'

import { useTranslation } from 'next-i18next'

import Button from 'components/Button'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Form from 'components/Form/Form'
import Password from 'components/Form/Password/Password'
import SubmitButton from 'components/SubmitButton'
import ValidationPointsList from 'components/ValidationPoint/ValidationPointsList'

import styles from './ChangePasswordForm.module.scss'

const ChangePasswordForm = ({ token, referrer }) => {
  const [t] = useTranslation(['error'])

  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isPointsVisible, toggleIsPointsVisible] = useState(false)
  const showValidationPoints = useCallback(() => toggleIsPointsVisible(true), [])

  const captchaRef = useRef(null)

  const onPasswordChange = useCallback(
    async (values) => {
      const captcha = await captchaRef.current.executeAsync()

      try {
        const { newPassword } = values
        const res = await changePassword({ token, newPassword, captcha })
        if (res.status !== 204) {
          const { code, payload } = res.data
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleValidationError(res)
                setError(wrappedErrors)
              }
              break
            case ErrorCode.RECOVERY_INVALID_CONFIRMATION_CODE:
              setError(validateMessage.invalidConfirmationCode)
              break
            case ErrorCode.RECOVERY_INCORRECT_CONFIRMATION_CODE:
              setError(validateMessage.incorrectConfirmationCode)
              break
            default:
              setError(validateMessage.serverError)
          }
        } else {
          setError('')
          setSuccess(true)
        }
      } catch (e) {
        setError(validateMessage.serverError)
      }
    },
    [token],
  )

  return (
    <>
      {success ? (
        <div className={styles.wrapper}>
          <div className={styles.success}>
            <img src="/images/util/done.svg" alt="success" />
            <p className={styles.tip}>Password successfully changed</p>
          </div>

          <Button id="changePassword-form-button-link" href="/auth/signin">
            Sign In
          </Button>
        </div>
      ) : (
        <Formik
          initialValues={{
            token,
            newPassword: '',
            confirmPassword: '',
          }}
          validationSchema={yup.object({
            newPassword: yup
              .string()
              .required('')
              .min(8, '')
              .max(32, '')
              .test('newPassword', '', (value) => {
                return (
                  /[0-9]/.test(value) &&
                  /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~№]/.test(value) &&
                  !value.includes(' ') &&
                  /[A-Z]/.test(value) &&
                  /[a-z]/.test(value) &&
                  onlyAllowedAndLatinLetters(value)
                )
              }),
            confirmPassword: yup
              .string()
              .required('')
              .oneOf([yup.ref('newPassword')], ''),
          })}
          onSubmit={onPasswordChange}
        >
          <Form>
            <Password
              referrer={referrer}
              name="newPassword"
              placeholder={t('contacts:newPassword')}
              id="changePassword-form-newPassword-input"
              onFocus={showValidationPoints}
            />
            <Password
              referrer={referrer}
              name="confirmPassword"
              placeholder={t('contacts:confirmPassword')}
              id="changePassword-form-conirmPassword-input"
              onFocus={showValidationPoints}
            />
            {error && <ErrorMessage message={error} />}
            {isPointsVisible && <ValidationPointsList t={t} />}

            <SubmitButton id="changePassword-form-submit-button">Change password</SubmitButton>
          </Form>
        </Formik>
      )}

      <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA} ref={captchaRef} size="invisible" />
    </>
  )
}

export default ChangePasswordForm
